import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  PercentIcon,
  Timer,
  Shield,
  Star,
  ArrowRight,
  GaugeCircle,
  BellRing,
} from "lucide-react";
import FeatureCard from "../components/FeatureCard";
import TestimonialCard from "../components/TestimonialCard";
import SpeakToProfessionalModal from "../components/SpeakToProfessionalModal";

const stateName = import.meta.env.VITE_STATE_NAME || "";

const HomePage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <main>
      <section className="bg-primary-light/10 pt-8 sm:pt-20 pb-4 sm:pb-10">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6">
            Could you get a better deal on your mortgage?
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-8">
            Upload your broker's quote and see how it stacks up—anonymously. No
            personal info required.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <div className="flex flex-col gap-4 flex-wrap items-center sm:w-1/3">
              <Link
                to="/check-my-rate"
                className="btn-primary px-6 py-3 text-lg rounded-lg sm:rounded-full"
              >
                Compare My Quote
              </Link>
              <Link
                to="/is-my-quote-good"
                className="btn-tertiary px-6 pt-3 text-lg rounded-md"
              >
                How It Works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section
        className="py-8 sm:py-16 bg-white"
        aria-labelledby="tools-heading"
      >
        <div className="container-custom">
          <div className="text-center mb-6 sm:mb-12">
            <h2
              id="tools-heading"
              className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-2"
            >
              Anonymous Mortgage Tools
            </h2>
            <p className="text-lg text-gray-600">
              None of our tools ask for personal information.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
            {/* Rate Check (Broker Review) */}
            <Link
              to="/check-my-rate"
              className="card h-full flex flex-col border border-gray-100 hover:shadow-lg transition-all duration-300 p-6 hover:border-primary-light/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-full bg-primary-light/10 p-3">
                  <GaugeCircle size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-heading font-semibold text-gray-800">
                  Quote Review
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Already working with a broker? Upload your quote and we'll grade
                it against local lenders anonymously.
              </p>
              <div className="mt-auto flex items-center justify-between gap-4">
                <Link
                  to="/is-my-quote-good"
                  className="btn-secondary"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
                <Link
                  to="/check-my-rate"
                  className="btn-primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  Compare My Quote
                </Link>
              </div>
            </Link>

            <Link
              to="/quote-start"
              className="card h-full flex flex-col border border-gray-100 hover:shadow-lg transition-all duration-300 p-6 hover:border-primary-light/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-full bg-primary-light/10 p-3">
                  <PercentIcon size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-heading font-semibold text-gray-800">
                  Shop Rates
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Easily compare mortgage rates across lenders. We will show you
                the best rates from local lenders, guaranteed.
              </p>
              <div className="mt-auto flex justify-end">
                <Link
                  to="/quote-start"
                  className="btn-primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  Check Rates
                </Link>
              </div>
            </Link>

            {/* Refi Buddy (Coming Soon) */}
            <Link
              to="/should-i-refinance"
              className="card h-full flex flex-col border border-gray-100 hover:shadow-lg transition-all duration-300 p-6 hover:border-primary-light/30 cursor-pointer relative"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-full bg-primary-light/10 p-3">
                  <BellRing size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-heading font-semibold text-gray-800">
                  Refi Buddy
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Get alerts when it's a good time to refinance. We'll track the
                market for you and notify you when it makes sense.
              </p>
              <div className="mt-auto flex items-center justify-end gap-4">
                <span className="btn-primary">Get Started</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        className="py-4 sm:py-16 bg-gray-50"
        aria-labelledby="why-choose-us-heading"
      >
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2
              id="why-choose-us-heading"
              className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4"
            >
              Why {stateName} Homeowners Trust Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide transparent, honest mortgage information without the
              sales pressure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={Timer}
              title="Fast & Simple"
              description="Our tools take just minutes to use, and you'll get detailed results within 24 hours."
            />

            <FeatureCard
              icon={Shield}
              title="Private & Anonymous"
              description="No personal information required. Compare rates without sharing your contact details."
            />

            <FeatureCard
              icon={Star}
              title="Expert Analysis"
              description="Get professional insights about your current rate from local mortgage experts."
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        className="py-4 sm:py-16 bg-white"
        aria-labelledby="testimonials-heading"
      >
        <div className="container-custom">
          <div className="text-center mb-6 sm:mb-12">
            <h2
              id="testimonials-heading"
              className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4"
            >
              What People Are Saying
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from {stateName} homeowners who have used our tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TestimonialCard
              className="h-full"
              quote="I saved $7,500 by not using the broker from the billboards. Their rate comparison tool showed me exactly how much I was overpaying."
              author="Jennifer M."
              // role="Homeowner in Salt Lake City"
              rating={5}
            />

            <TestimonialCard
              className="h-full"
              quote="The pros and cons analysis was incredibly helpful. It was easy to see that my rate was higher than it needed to be."
              author="Emily R."
              // role="Homeowner in Provo"
              rating={5}
            />

            <TestimonialCard
              className="h-full"
              quote="It was so nice to be able to double check my rate and fees without giving any of my personal information."
              author="David L."
              // role="Homeowner in Ogden"
              rating={5}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-8 sm:py-16 bg-primary text-white"
        aria-labelledby="cta-heading"
      >
        <div className="container-custom text-center">
          <h2
            id="cta-heading"
            className="text-3xl md:text-4xl font-heading font-bold mb-6"
          >
            Ready to Get a Second Opinion?
          </h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            It takes less than 2 minutes, and you don't need to provide any
            personal information.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/check-my-rate"
              className="btn-primary-on-dark font-medium py-3 px-6 rounded-full shadow-lg transition-all duration-300"
              aria-label="Start rate comparison"
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>

      {/* Speak to a Professional Section */}
      <section
        className="py-8 sm:py-16 bg-gray-50"
        aria-labelledby="contact-heading"
      >
        <div className="container-custom text-center">
          <h2
            id="contact-heading"
            className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4"
          >
            Have Questions?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Our mortgage experts are ready to help you, even if you already have
            your own broker. Get unbiased advice and make confident decisions.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary inline-flex items-center justify-center space-x-2 rounded-full py-3 px-6"
            aria-label="Speak to a mortgage expert"
          >
            <span>Speak to a Mortgage Expert</span>
            <ArrowRight size={18} aria-hidden="true" />
          </button>
        </div>
      </section>

      <SpeakToProfessionalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </main>
  );
};

export default HomePage;
